// Rendu des éléments (terrains et objets) d'après leur définition, partagé par le jeu et l'administration :
// calques, variantes, raccords « blob » par quarts de case, barrières, animations, élément dessous et
// débordements dessinés avec les personnages.
import {
  BLOB_QUARTERS, blobPiece, blobTile, connects, DEFAULT_BORDER_DIRECTION, groupsSeen, MAP_MARGIN, outerKey, coveredCells, directionalFrames, isDirectional, isMirrored, EMPTY_TILE, indexCells, borderSprite, stripPiece, linePiece, TILESET_URL, trailPiece, triggerFrame, turnStep, useDuration, type CellDepth,
  type CellIndex, type ElementLayer, type BorderDirection, type MapElement, type MapLayout, type MapObject, type SpriteRef, type Turns,
} from '../../shared/map-elements.ts';
import type { ObjectPose } from '../../shared/object-motion.ts';
import type { Direction } from '../../shared/protocol.ts';

// ---------- Images des assets (tilesets, planches de personnages, effets de combat) ----------

const images = new Map<string, HTMLImageElement>();
const loadListeners = new Set<() => void>();

/** Image d'un dossier d'assets (`folder` : URL finissant par « / »), chargée une seule fois. */
export function assetImage(folder: string, file: string): HTMLImageElement {
  const url = `${folder}${encodeURIComponent(file)}`;
  let image = images.get(url);
  if (!image) {
    image = new Image();
    image.addEventListener('load', () => { for (const listener of loadListeners) listener(); });
    image.src = url;
    images.set(url, image);
  }
  return image;
}

/** Recharge une image remplacée sous le même nom (l'ancienne reste sinon en cache). */
export function reloadAssetImage(folder: string, file: string): void {
  const url = `${folder}${encodeURIComponent(file)}`;
  const image = new Image();
  image.addEventListener('load', () => { for (const listener of loadListeners) listener(); });
  image.src = `${url}?v=${Date.now()}`;
  images.set(url, image);
}

/** Image d'un tileset, chargée une seule fois. */
export const tilesetImage = (file: string): HTMLImageElement => assetImage(TILESET_URL, file);

/** `listener` est appelé chaque fois qu'une image d'assets finit de charger (pour redessiner). */
export function onTilesetImagesLoaded(listener: () => void): () => void {
  loadListeners.add(listener);
  return () => loadListeners.delete(listener);
}

// ---------- Scène : une carte et ses éléments ----------

/** Une carte prête à dessiner : sa disposition, les éléments connus et l'index de ses cases. */
export interface Scene extends MapLayout {
  elements: ReadonlyMap<number, MapElement>;
  cells: CellIndex;
  /** Débordement maximal d'un élément au-delà de ses cases, pour élargir les zones redessinées. */
  overflow: Overflow;
  /** Rotation des cases de terrain tournées, par case de chaque calque (voir terrainKey). */
  terrainTurns: ReadonlyMap<number, Turns>;
  /** Terrains des cases de la marge (voir outerKey), calque par calque (vide : rien sur ce calque). */
  outerTerrains: ReadonlyMap<string, readonly ({ id: number; turns: Turns } | undefined)[]>;
}

export interface Overflow {
  left: number;
  right: number;
  up: number;
  down: number;
}

export function createScene(layout: MapLayout, elements: ReadonlyMap<number, MapElement>): Scene {
  const terrainTurns = new Map(layout.rotations.map(({ layer, x, y, turns }) => [terrainKey(layout, layer, y * layout.width + x), turns]));
  const outerTerrains = new Map<string, ({ id: number; turns: Turns } | undefined)[]>();
  for (const { x, y, layer, tileId, turns } of layout.outerTiles ?? []) {
    const key = outerKey(x, y);
    const layers = outerTerrains.get(key) ?? [];
    layers[layer] = { id: tileId, turns };
    outerTerrains.set(key, layers);
  }
  return { ...layout, elements, cells: indexCells(layout, elements), overflow: overflowOf(elements), terrainTurns, outerTerrains };
}

/** Clé d'une case d'un calque de terrain dans `terrainTurns`. */
const terrainKey = (layout: Pick<MapLayout, 'width' | 'height'>, layer: number, index: number) => layer * layout.width * layout.height + index;

/** De combien de cases (au plus) les calques des éléments débordent de leurs cases. */
function overflowOf(elements: ReadonlyMap<number, MapElement>): Overflow {
  const overflow = { left: 0, right: 0, up: 0, down: 0 };
  for (const element of elements.values()) {
    const g = element.gridSize;
    for (const layer of element.layers) {
      overflow.left = Math.max(overflow.left, Math.ceil(-layer.offsetX / g));
      overflow.up = Math.max(overflow.up, Math.ceil(-layer.offsetY / g));
      overflow.right = Math.max(overflow.right, Math.ceil((layer.offsetX + layer.width) / g) - element.width);
      overflow.down = Math.max(overflow.down, Math.ceil((layer.offsetY + layer.height) / g) - element.height);
    }
    // Tourné, ce qui déborde d'un côté peut déborder de n'importe quel autre.
    if (turnStep(element)) {
      const most = Math.max(overflow.left, overflow.right, overflow.up, overflow.down);
      Object.assign(overflow, { left: most, right: most, up: most, down: most });
    }
  }
  return overflow;
}

/** Quels calques dessiner : ceux du sol, ou ceux triés avec les personnages. */
/**
 * Passes de dessin : `ground` sous les personnages (calques de sol, cases « derrière »), `sorted` trié avec
 * eux par ligne (cases « trié »), `front` par-dessus tous les personnages (cases « devant »).
 */
export type Pass = 'ground' | 'sorted' | 'front';

const PASS_DEPTH: Readonly<Record<Pass, CellDepth>> = { ground: 'behind', sorted: 'sort', front: 'front' };
/** Au-delà du bord de l'élément, une case du bord s'étend « à l'infini » (débordements d'un arbre, d'un toit). */
const BEYOND = 100_000;

interface Placement {
  scene: Scene | null;
  /** Case d'ancrage (terrain : sa case ; objet : son coin supérieur gauche). */
  x: number;
  y: number;
  /** Taille en cases de ce qui est dessiné, pour trouver les voisins. */
  width: number;
  height: number;
  /** Calque du terrain : il ne se raccorde qu'aux terrains de ce calque (et aux objets) ; absent (objet) : à tout. */
  layer?: number;
  /** Sens de l'objet mobile (calques « par sens ») ; par défaut, le bas. */
  dir?: Direction;
  /** Marque de trace à dessiner (seuls les calques « Trace » le sont alors) ; absente : les autres calques. */
  trail?: TrailMark;
}

/** Passage d'un personnage sur une case : sens dans lequel il y est arrivé, et reparti (null : départ, ou encore dessus). */
export interface TrailMark {
  entry: Direction | null;
  exit: Direction | null;
}

class ElementPainter {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly time: number;
  /** Ouverture des calques à déclenchement (0 : fermé, sur la première image ; 1 : ouvert, sur la dernière). */
  private readonly opened: number;
  /** Temps écoulé depuis que le joueur s'est servi de l'objet, en ms (undefined : pas en cours) : calques « à l'utilisation ». */
  private readonly used: number | undefined;
  /** Temps écoulé depuis le départ du véhicule, en ms (undefined : à quai) : calques « au départ » (une fois ou en boucle). */
  private readonly departed: number | undefined;

  constructor(ctx: CanvasRenderingContext2D, time: number, opened = 0, used?: number, departed?: number) {
    this.ctx = ctx;
    this.time = time;
    this.opened = opened;
    this.used = used;
    this.departed = departed;
  }

  /** Image d'une animation de `count` images de `ms` chacune : en boucle, ou arrêtée là où en est l'ouverture. */
  private frameOf(layer: ElementLayer, count: number, ms: number, phase: number): number {
    if (count <= 1) return 0;
    if (layer.playback === 'trigger') return triggerFrame(count, this.opened);
    if (layer.playback === 'use') return this.used === undefined || this.used >= useDuration(layer) ? 0 : Math.min(count - 1, Math.floor(this.used / ms));
    if (layer.playback === 'depart' || layer.playback === 'departLoop') {
      const elapsed = Math.max(0, (this.departed ?? 0) - layer.departDelayMs);
      return layer.playback === 'depart' ? Math.min(count - 1, Math.floor(elapsed / ms)) : Math.floor(elapsed / ms) % count;
    }
    return (Math.floor(this.time / ms) + Math.floor(phase * count)) % count;
  }

  /**
   * Le calque est-il en train de jouer ? À l'utilisation : pas au repos, ni une fois toutes les images jouées ; au
   * départ : pas à quai ni pendant le délai ; au déclenchement : pas tout fermé. Au repos, il n'est dessiné (sur sa
   * première image) que si `idleFirstFrame`.
   */
  private playing(layer: ElementLayer): boolean {
    switch (layer.playback) {
      case 'loop': return true;
      case 'trigger': return this.opened > 0;
      case 'use': return this.used !== undefined && this.used < useDuration(layer);
      case 'depart': case 'departLoop': return this.departed !== undefined && this.departed >= layer.departDelayMs;
    }
  }

  /** Dessine `element` avec son coin en (px, py), `cell` pixels par case. */
  draw(element: MapElement, at: Placement, px: number, py: number, cell: number, pass: Pass): void {
    // Terrain dessous d'un objet (un terrain n'en a pas lui-même) : il occupe chaque case de l'objet.
    const underlay = element.underlay === null ? undefined : at.scene?.elements.get(element.underlay);
    if (underlay && underlay.underlay === null) {
      for (let dy = 0; dy < element.height; dy++) {
        for (let dx = 0; dx < element.width; dx++) {
          this.draw(underlay, { ...at, x: at.x + dx, y: at.y + dy, width: 1, height: 1 }, px + dx * cell, py + dy * cell, cell, pass);
        }
      }
    }
    if (at.trail) {
      element.layers.forEach((layer, index) => { if (layer.shape.mode === 'trail') this.drawLayer(element, layer, index, at, px, py, cell); });
      return;
    }
    const wanted = PASS_DEPTH[pass];
    const cellDepths = element.depth ?? [];
    // Cas courant : toutes les cases sont « trié », les calques concernés sont simplement dessinés avec les personnages.
    const uniform = cellDepths.every((value) => value === 'sort');
    const clipped = !uniform && cellDepths.includes(wanted);
    element.layers.forEach((layer, index) => {
      if (!layer.sorted) {
        if (pass === 'ground') this.drawLayer(element, layer, index, at, px, py, cell);
        return;
      }
      if (uniform ? wanted !== 'sort' : !clipped) return;
      if (uniform) return this.drawLayer(element, layer, index, at, px, py, cell);
      this.ctx.save();
      this.clipToDepth(element, cellDepths, wanted, px, py, cell);
      this.drawLayer(element, layer, index, at, px, py, cell);
      this.ctx.restore();
    });
  }

  /** Limite le dessin aux cases de l'élément qui ont cette profondeur (cases du bord étendues au-delà). */
  private clipToDepth(element: MapElement, depth: readonly CellDepth[], wanted: CellDepth, px: number, py: number, cell: number): void {
    const { ctx } = this;
    const { width, height } = element;
    ctx.beginPath();
    depth.forEach((value, i) => {
      if (value !== wanted) return;
      const cx = i % width, cy = Math.floor(i / width);
      const x0 = cx === 0 ? px - BEYOND : px + cx * cell, x1 = cx === width - 1 ? px + BEYOND : px + (cx + 1) * cell;
      const y0 = cy === 0 ? py - BEYOND : py + cy * cell, y1 = cy === height - 1 ? py + BEYOND : py + (cy + 1) * cell;
      ctx.rect(x0, y0, x1 - x0, y1 - y0);
    });
    ctx.clip();
  }

  private drawLayer(element: MapElement, layer: ElementLayer, index: number, at: Placement, px: number, py: number, cell: number): void {
    if (!layer.idleFirstFrame && !this.playing(layer)) return;
    const scale = cell / element.gridSize;
    const dx = px + layer.offsetX * scale, dy = py + layer.offsetY * scale;
    const dw = layer.width * scale, dh = layer.height * scale;
    const { shape } = layer;
    const connected = (sx: number, sy: number, beyondMap = true) => this.connected(element, at, sx, sy, beyondMap);
    // Départ aléatoire : décalage (0..1) propre à la case, pour que les animations voisines ne battent pas ensemble.
    const phase = layer.randomStart ? noise(at.x, at.y, index + 31) : 0;
    switch (shape.mode) {
      case 'single':
      case 'directional': {
        // Par sens : les images du sens de l'objet ; elles s'animent comme une image unique.
        const sprites = shape.mode === 'single' ? shape.sprites : directionalFrames(shape.sprites, at.dir ?? 'down');
        const count = sprites.length;
        const sprite = sprites[this.frameOf(layer, count, layer.frameMs, phase)];
        if (sprite) this.sprite(sprite, layer, 0, 0, layer.width, layer.height, dx, dy, dw, dh, phase);
        return;
      }
      case 'variants': {
        const total = shape.variants.reduce((sum, variant) => sum + variant.weight, 0);
        let roll = noise(at.x, at.y, index) * total;
        const chosen = shape.variants.find((variant) => (roll -= variant.weight) < 0) ?? shape.variants.at(-1);
        if (chosen) this.sprite(chosen.sprite, layer, 0, 0, layer.width, layer.height, dx, dy, dw, dh, phase);
        return;
      }
      case 'lines': {
        const piece = linePiece(connected(0, -1), connected(1, 0), connected(0, 1), connected(-1, 0));
        const corner = piece === 'single' || piece === 'horizontal' || piece === 'vertical' || piece === 'junction' ? null : shape.corners[piece];
        const pieces = piece === 'single' ? [shape.single]
          : piece === 'horizontal' ? [shape.horizontal]
            : piece === 'vertical' ? [shape.vertical]
              : corner ? [corner] : [shape.horizontal, shape.vertical];
        for (const piece of pieces) if (piece) this.sprite(piece, layer, 0, 0, layer.width, layer.height, dx, dy, dw, dh, phase);
        return;
      }
      case 'strip': {
        // Comme un blob, la ligne ne se prolonge pas au-delà du bord de la carte.
        const { pieces } = shape;
        const [sx, sy] = shape.axis === 'vertical' ? [0, 1] : [1, 0];
        const sprite = pieces[stripPiece(connected(-sx, -sy, false), connected(sx, sy, false))] ?? pieces.middle;
        if (sprite) this.sprite(sprite, layer, 0, 0, layer.width, layer.height, dx, dy, dw, dh, phase);
        return;
      }
      case 'trail': {
        // Dessinée seulement là où l'on est passé : pivotée (et retournée pour un virage de l'autre côté) autour de la case.
        if (!at.trail) return;
        const { piece, quarters, mirror } = trailPiece(at.trail.entry, at.trail.exit);
        const sprite = piece === 'turn' ? (shape.turn ?? shape.straight) : shape.straight;
        if (!sprite) return;
        const flip = mirror && shape.turn !== null;
        const cx = px + (element.width * cell) / 2, cy = py + (element.height * cell) / 2;
        const { ctx } = this;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate((quarters * Math.PI) / 2);
        if (flip) ctx.scale(-1, 1);
        ctx.translate(-cx, -cy);
        this.sprite(sprite, layer, 0, 0, layer.width, layer.height, dx, dy, dw, dh, phase);
        ctx.restore();
        return;
      }
      case 'border': {
        // Sens de la case (aperçus sans carte : vers le bas) ; un coin intérieur sans image reprend celle de l'angle.
        const piece = (at.scene && borderSprite(at.scene.cells.borders, at.scene.width, at.x, at.y)) ?? DEFAULT_BORDER_DIRECTION;
        const sprite = shape.sprites[piece] ?? (piece.startsWith('inner-') ? shape.sprites[piece.slice('inner-'.length) as BorderDirection] : null);
        if (sprite) this.sprite(sprite, layer, 0, 0, layer.width, layer.height, dx, dy, dw, dh, phase);
        return;
      }
      case 'blob': {
        const hw = layer.width / 2, hh = layer.height / 2;
        // Le bord de la carte n'est pas un voisin semblable : l'eau, le chemin… y ont leur bordure.
        const quarters = BLOB_QUARTERS.map(([sx, sy]) => blobPiece(connected(sx, 0, false), connected(0, sy, false), connected(sx, sy, false), sx, sy));
        // Case qui correspond à une pièce entière (coin, bord, centre) : ses quatre quarts viennent de cette pièce.
        const tile = blobTile(quarters);
        const whole = tile ? shape.pieces[tile] : undefined;
        BLOB_QUARTERS.forEach(([sx, sy], i) => {
          const piece = quarters[i]!;
          const sprite = whole ?? shape.pieces[piece] ?? (piece.startsWith('i') ? shape.pieces.center : undefined);
          if (!sprite) return;
          const qx = sx < 0 ? 0 : hw, qy = sy < 0 ? 0 : hh;
          this.sprite(sprite, layer, qx, qy, hw, hh, dx + qx * scale, dy + qy * scale, hw * scale, hh * scale, phase);
        });
      }
    }
  }

  /** Le voisin (sx, sy) se raccorde-t-il ? `beyondMap` : réponse pour une case hors de la carte (une barrière s'y prolonge). */
  private connected(element: MapElement, { scene, x, y, width, height, layer }: Placement, sx: number, sy: number, beyondMap: boolean): boolean {
    if (!scene) return false;
    const nx = sx < 0 ? x - 1 : sx > 0 ? x + width : x;
    const ny = sy < 0 ? y - 1 : sy > 0 ? y + height : y;
    // Hors de la carte : le terrain (ou l'objet) posé dans la marge, sinon la réponse par défaut.
    if (nx < 0 || ny < 0 || nx >= scene.width || ny >= scene.height) {
      const outer = scene.cells.outerGroups.get(outerKey(nx, ny));
      return outer ? seenConnects(element, groupsSeen(outer, layer)) : beyondMap;
    }
    return seenConnects(element, groupsSeen(scene.cells.groups.get(ny * scene.width + nx) ?? [], layer));
  }

  /**
   * Une partie (qx, qy, w, h) d'un morceau d'image, animé par bande s'il y a lieu, dessinée dans le rectangle
   * donné ; `phase` (0..1) décale le départ de l'animation.
   */
  private sprite(sprite: SpriteRef, layer: ElementLayer, qx: number, qy: number, w: number, h: number,
    dx: number, dy: number, dw: number, dh: number, phase: number): void {
    const { ctx } = this;
    const image = tilesetImage(sprite.image);
    if (!image.complete || !image.naturalWidth) return;
    const { animation } = layer;
    const frame = animation ? this.frameOf(layer, animation.frames, animation.ms, phase) : 0;
    const sx = sprite.x + qx + frame * (layer.animation?.dx ?? 0), sy = sprite.y + qy + frame * (layer.animation?.dy ?? 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(image, sx, sy, w, h, dx, dy, dw, dh);
  }
}

/** Un voisin sans rien sur le calque (ni objet) n'est pas un semblable : le terrain s'y arrête, avec sa bordure. */
function seenConnects(element: MapElement, groups: readonly string[]): boolean {
  return groups.length > 0 && connects(element, groups);
}

/** Valeur pseudo-aléatoire stable (0..1) propre à une case. */
function noise(x: number, y: number, k: number): number {
  let h = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(k + 1, 982451653);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

// ---------- Points d'entrée ----------

/**
 * Terrains de la case (x, y) et leur rotation : tous (du sol vers le haut, cases vides omises), ou seulement
 * celui d'un calque (s'il y en a un). Hors de la carte, ceux peints dans la marge.
 */
export function cellTerrains(scene: Scene, x: number, y: number, layer?: number): { id: number; turns: Turns; layer: number }[] {
  const layers = layer === undefined ? Array.from({ length: 1 + scene.upperTiles.length }, (_, k) => k) : [layer];
  if (x < 0 || y < 0 || x >= scene.width || y >= scene.height) {
    const outer = scene.outerTerrains.get(outerKey(x, y));
    return outer ? layers.flatMap((k) => (outer[k] ? [{ ...outer[k], layer: k }] : [])) : [];
  }
  const index = y * scene.width + x;
  return layers.flatMap((k) => {
    const id = (k === 0 ? scene.tiles : scene.upperTiles[k - 1])?.[index] ?? EMPTY_TILE;
    return id === EMPTY_TILE ? [] : [{ id, turns: scene.terrainTurns.get(terrainKey(scene, k, index)) ?? 0, layer: k }];
  });
}

/** Dessine avec le canevas tourné de `turns` quarts de tour autour de (cx, cy), retourné gauche-droite en miroir (voir isMirrored). */
function turned(ctx: CanvasRenderingContext2D, turns: Turns, cx: number, cy: number, draw: () => void): void {
  if (!turns) return draw();
  ctx.save();
  ctx.translate(cx, cy);
  if (isMirrored(turns)) ctx.scale(-1, 1);
  else ctx.rotate((turns * Math.PI) / 2);
  ctx.translate(-cx, -cy);
  draw();
  ctx.restore();
}

/** Ouverture (0 à 1) des calques à déclenchement d'un terrain sur une case (voir drawPlacedObject). */
export type TerrainOpening = (x: number, y: number, element: MapElement) => number;

/**
 * Terrains de la case (x, y), du sol vers les calques du dessus (`layer` : celui de ce calque seulement), coin en
 * (px, py), `cell` pixels par case (idéalement un multiple de 16). Un terrain est toujours à plat, sous les objets
 * et les personnages. `opened` : ouverture de ses calques à déclenchement (absent : fermés).
 */
export function drawTerrainCell(ctx: CanvasRenderingContext2D, scene: Scene, x: number, y: number, px: number, py: number, cell: number, time: number, layer?: number,
  opened?: TerrainOpening): void {
  for (const { id, turns, layer: k } of cellTerrains(scene, x, y, layer)) {
    const element = scene.elements.get(id);
    if (element) {
      const painter = new ElementPainter(ctx, time, opened?.(x, y, element) ?? 0);
      turned(ctx, turns, px + cell / 2, py + cell / 2, () => painter.draw(element, { scene, x, y, width: 1, height: 1, layer: k }, px, py, cell, 'ground'));
    } else {
      ctx.fillStyle = '#ff00ff';
      ctx.fillRect(px, py, cell, cell);
    }
  }
}

/** Marque de trace laissée sur la case (x, y) par un personnage qui a traversé le terrain `element`, coin en (px, py). */
export function drawTrailMark(ctx: CanvasRenderingContext2D, scene: Scene, element: MapElement, x: number, y: number, px: number, py: number, cell: number, time: number,
  trail: TrailMark): void {
  new ElementPainter(ctx, time).draw(element, { scene, x, y, width: 1, height: 1, trail }, px, py, cell, 'ground');
}

/**
 * Terrain des cases (x0, y0)–(x1, y1), calque par calque (le sol partout, puis le calque 1, puis le 2), y compris ce
 * qui y déborde depuis les cases voisines ; `layer` : ce calque seulement. `at` donne le coin d'une case à l'écran.
 */
export function drawTerrainArea(ctx: CanvasRenderingContext2D, scene: Scene, x0: number, y0: number, x1: number, y1: number,
  at: (x: number, y: number) => { px: number; py: number }, cell: number, time: number, layer?: number, opened?: TerrainOpening): void {
  const { overflow } = scene;
  // Le terrain de la marge déborde aussi dans la carte (le jeu coupe le reste au bord).
  const margin = scene.outerTerrains.size ? MAP_MARGIN : 0;
  const top = Math.max(-margin, y0 - overflow.down), bottom = Math.min(scene.height - 1 + margin, y1 + overflow.up);
  const left = Math.max(-margin, x0 - overflow.right), right = Math.min(scene.width - 1 + margin, x1 + overflow.left);
  const layers = layer === undefined ? Array.from({ length: 1 + scene.upperTiles.length }, (_, k) => k) : [layer];
  for (const k of layers) {
    for (let y = top; y <= bottom; y++) {
      for (let x = left; x <= right; x++) {
        const { px, py } = at(x, y);
        drawTerrainCell(ctx, scene, x, y, px, py, cell, time, k, opened);
      }
    }
  }
}

/**
 * Objet posé, coin en (px, py), tourné s'il l'a été en le posant ; `orientation` : image pivotée ou retournée autour de son centre (objet mobile) ;
 * `opened` : ouverture de ses calques à déclenchement (porte), de 0 (ou absent : fermés) à 1 (ouverts) ; `used` : temps
 * écoulé depuis que le joueur s'en est servi, en ms (absent : pas en cours), pour ses calques « à l'utilisation ».
 */
export function drawPlacedObject(ctx: CanvasRenderingContext2D, scene: Scene, object: MapObject, px: number, py: number, cell: number, time: number, pass: Pass,
  orientation?: Pick<ObjectPose, 'angle' | 'flipX' | 'flipY'> & { dir?: Direction }, opened = 0, used?: number, departed?: number): void {
  const element = scene.elements.get(object.elementId);
  if (!element) return;
  const at = { scene, x: object.x, y: object.y, width: element.width, height: element.height, dir: orientation?.dir };
  const hw = (element.width * cell) / 2, hh = (element.height * cell) / 2;
  // Un élément « par sens » a une image pour chaque sens : il n'est ni retourné ni pivoté.
  if (!orientation || isDirectional(element) || (!orientation.angle && !orientation.flipX && !orientation.flipY)) {
    turned(ctx, object.turns, px + hw, py + hh, () => new ElementPainter(ctx, time, opened, used, departed).draw(element, at, px, py, cell, pass));
    return;
  }
  ctx.save();
  ctx.translate(px + hw, py + hh);
  ctx.rotate(orientation.angle);
  ctx.scale(orientation.flipX ? -1 : 1, orientation.flipY ? -1 : 1);
  new ElementPainter(ctx, time, opened, used, departed).draw(element, at, -hw, -hh, cell, pass);
  ctx.restore();
}


/** Cases couvertes par un objet (pour les zones redessinées de l'éditeur). */
export const objectCells = coveredCells;

/**
 * Aperçu isolé d'un élément dans un carré de `size` pixels : terrain répété en 1 case, objet réduit
 * pour tenir, débordements compris.
 */
export function drawElementPreview(ctx: CanvasRenderingContext2D, element: MapElement, elements: ReadonlyMap<number, MapElement>, size: number, time = 0): void {
  ctx.clearRect(0, 0, size, size);
  const layout: MapLayout = element.kind === 'terrain'
    ? { width: 1, height: 1, tiles: [element.id], upperTiles: [], objects: [], borders: [], rotations: [] }
    : { width: element.width, height: element.height, tiles: new Array<number>(element.width * element.height).fill(-1), upperTiles: [], objects: [{ elementId: element.id, x: 0, y: 0, layer: 0, turns: 0, motion: null, text: '', drop: null, use: null, shop: null, presence: null, trip: null, mechanism: null }], borders: [], rotations: [] };
  const scene = createScene(layout, new Map([...elements, [element.id, element]]));
  // Cadre englobant les débordements, pour que l'arbre entier tienne dans l'aperçu.
  const g = element.gridSize;
  let left = 0, top = 0, right = element.width * g, bottom = element.height * g;
  for (const layer of element.layers) {
    left = Math.min(left, layer.offsetX); top = Math.min(top, layer.offsetY);
    right = Math.max(right, layer.offsetX + layer.width); bottom = Math.max(bottom, layer.offsetY + layer.height);
  }
  const scale = size / Math.max(right - left, bottom - top);
  const cell = g * scale;
  const px = (size - (right - left) * scale) / 2 - left * scale, py = (size - (bottom - top) * scale) / 2 - top * scale;
  const painter = new ElementPainter(ctx, time);
  for (const pass of ['ground', 'sorted', 'front'] as const) {
    painter.draw(element, { scene, x: 0, y: 0, width: element.width, height: element.height }, px, py, cell, pass);
  }
}
